import { useGetRequestProducts, useFilterRequestProducts, useGetRequestProductsStats, useUpdateRequestProductStatus } from '@practium/features/api';
import type { RequestProduct } from '@practium/features/products/types';
import { fuzzyFilter, useDebounce } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import { useToast } from '@practium/ui';
import { useEffect, useMemo, useState } from 'react';
import type { ProductRequestsTab } from '../types/productRequest.types';
import { CATEGORY_LABELS } from '../types/productRequest.types';

const PAGE_SIZE = 10;
const REQUESTS_REFETCH_INTERVAL = 15_000;

const STATUS_TAB_MAP = { pending: 'PENDING', approved: 'APPROVED', rejected: 'REJECTED' } as const;

const STATUS_FILTER_GROUP: FilterGroup = {
	label: 'Status',
	selectionMode: 'single',
	options: [
		{ value: 'status:PENDING', label: 'Pendentes' },
		{ value: 'status:APPROVED', label: 'Aceitos' },
		{ value: 'status:REJECTED', label: 'Rejeitados' },
	],
};

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Categoria',
		options: [
			{ value: 'categoria:fertilizer', label: CATEGORY_LABELS.fertilizer },
			{ value: 'categoria:lime', label: CATEGORY_LABELS.lime },
			{ value: 'categoria:conditioner', label: CATEGORY_LABELS.conditioner },
			{ value: 'categoria:organic', label: CATEGORY_LABELS.organic },
			{ value: 'categoria:special', label: CATEGORY_LABELS.special },
			{ value: 'categoria:defensive', label: CATEGORY_LABELS.defensive },
		],
	},
	{
		label: 'Período',
		selectionMode: 'single',
		options: [
			{ value: 'periodo:15', label: 'Últimos 15 dias' },
			{ value: 'periodo:30', label: 'Últimos 30 dias' },
			{ value: 'periodo:custom', label: 'Personalizado' },
		],
	},
];

export const useProductRequestsPage = () => {
	const toast = useToast();
	const [activeTab, setActiveTab] = useState<ProductRequestsTab>('pending');
	const [searchQuery, setSearchQuery] = useState('');
	const searchContext = useMemo(() => ({ tab: activeTab, query: searchQuery.trim() }), [activeTab, searchQuery]);
	const debouncedSearch = useDebounce(searchContext, 400);
	const search = searchQuery.trim() && debouncedSearch.tab === activeTab ? debouncedSearch.query : '';
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [selectedRequest, setSelectedRequest] = useState<RequestProduct | null>(null);

	const categories = filterValues
		.filter(value => value.startsWith('categoria:'))
		.map(value => value.slice('categoria:'.length));
	const selectedPeriod = filterValues.find(value => value.startsWith('periodo:'));
	const period = selectedPeriod === 'periodo:15' ? 'last15Days'
		: selectedPeriod === 'periodo:30' ? 'last30Days'
		: selectedPeriod === 'periodo:custom' && customDateFrom && customDateTo ? 'custom'
		: undefined;
	const selectedStatus = filterValues.find(value => value.startsWith('status:'))?.slice('status:'.length);
	const statusFilter = selectedStatus === 'PENDING' || selectedStatus === 'APPROVED' || selectedStatus === 'REJECTED'
		? selectedStatus : undefined;
	const filterGroups = activeTab === 'all' ? [...FILTER_GROUPS, STATUS_FILTER_GROUP] : FILTER_GROUPS;
	const requestFilters = {
		categories: categories.length ? categories : undefined,
		status: activeTab === 'all' ? statusFilter : STATUS_TAB_MAP[activeTab],
		period,
		startDate: period === 'custom' ? customDateFrom : undefined,
		endDate: period === 'custom' ? customDateTo : undefined,
	} as const;

	const hasFilters = Boolean(requestFilters.categories || period);
	const filtersKey = JSON.stringify([activeTab, requestFilters, hasFilters ? searchQuery : search]);
	const [paginationState, setPaginationState] = useState({ filtersKey, page: 1 });
	const page = paginationState.filtersKey === filtersKey ? paginationState.page : 1;
	const setPage = (nextPage: number) => setPaginationState({ filtersKey, page: nextPage });

	const statsQuery = useGetRequestProductsStats({
		refetchInterval: REQUESTS_REFETCH_INTERVAL,
	});
	const listing = useGetRequestProducts({
		page,
		limit: PAGE_SIZE,
		enabled: !hasFilters,
		search: search || undefined,
		status: requestFilters.status,
		refetchInterval: REQUESTS_REFETCH_INTERVAL,
	});
	const filteredListing = useFilterRequestProducts({
		...requestFilters,
		page,
		limit: PAGE_SIZE,
		enabled: hasFilters,
		refetchInterval: REQUESTS_REFETCH_INTERVAL,
	});
	const activeQuery = hasFilters ? filteredListing : listing;
	const { data: requests, pagination } = activeQuery;
	const { mutateAsync: updateStatus } = useUpdateRequestProductStatus();

	const handleTabChange = (tab: ProductRequestsTab) => {
		setActiveTab(tab);
		setSearchQuery('');
		setFilterValues([]);
		setPage(1);
	};

	const processedRequests = useMemo(() => {
		let result = requests;

		// TODO: Use remote search when /request-products/filter supports it.
		result = hasFilters ? fuzzyFilter(result, searchQuery, r =>
			[r.name, r.manufacturer, r.userName, r.category].join(' ')
		) : result;

		if (sortAsc) {
			// TODO: Use remote ordering when request-products supports it.
			result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		}

		return result;
	}, [requests, searchQuery, sortAsc, hasFilters]);

	const totalPages = Math.max(1, pagination?.totalPages ?? 1);
	const canClampPage = activeQuery.isSuccess && !activeQuery.isPlaceholderData && activeQuery.fetchStatus === 'idle';
	const isPageOutOfRange = pagination !== null && page > totalPages;
	useEffect(() => {
		setPaginationState(current => {
			if (current.filtersKey !== filtersKey) return { filtersKey, page: 1 };
			if (canClampPage && current.page > totalPages) return { filtersKey, page: totalPages };
			return current;
		});
	}, [filtersKey, canClampPage, totalPages]);
	const filteredRequests = processedRequests;

	const handleUpdateStatus = async (status: 'APPROVED' | 'REJECTED') => {
		if (!selectedRequest) return;
		try {
			await updateStatus({ id: selectedRequest._id, status });
			setSelectedRequest(null);
			toast.success('Status atualizado com sucesso.');
		} catch {
			toast.error('Erro ao atualizar o status. Tente novamente.');
		}
	};

	return {
		filteredRequests,
		isTableLoading: activeQuery.isPending || activeQuery.isPlaceholderData || isPageOutOfRange,
		isTableFetching: activeQuery.isFetching,
		isTableError: activeQuery.isError,
		refetchTable: async () => {
			await activeQuery.refetch();
		},
		isStatsLoading: statsQuery.isPending,
		isStatsFetching: statsQuery.isFetching,
		isStatsError: statsQuery.isError,
		refetchStats: statsQuery.refetch,
		activeTab,
		setActiveTab: handleTabChange,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc(v => !v),
		filterValues,
		setFilterValues,
		filterGroups,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		page,
		setPage,
		totalPages,
		selectedRequest,
		setSelectedRequest,
		handleUpdateStatus,
		stats: statsQuery.data,
	};
};
